import React, {useRef, useState} from "react";
import {
    AppBar,
    Grid,
    Stack,
    useTheme,
    Drawer,
    Divider, Box,
} from "@mui/material";
import {
    AccountBox, AccountCircle, ArchiveOutlined, AssessmentOutlined,
    Logout, MailOutline,
    Menu as MenuIcon, Person,
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {SubmissionTranslKey} from "../submission/SubmissionTranslKey";
import {UserTranslKey} from "../user/UserTranslKey";
import {FormSessionTranslKey} from "../session/FormSessionTranslKey";
import {useAuth} from "../../auth/AuthContext";
import {useUserRoles} from "../user/UserContext";
import UserRoleResolver from "../user/UserRoleResolver";
import {AppBarIconButton} from "@dain/mui-components";
import {useNavigate} from "react-router-dom";
import {
    ADMIN_PERSON_OVERVIEW,
    ADMIN_SUBMISSIONS, ADMIN_USER_OVERVIEW,
    DASHBOARD,
    FORM_SESSIONS, ISDS_MESSAGE_OVERVIEW, ISDS_MESSAGE_STATE_OVERVIEW,
    PROFILE,
    SUBMISSIONS
} from "../../AppRoutes";
import {EnvConfiguration, PortalProfile} from "../../EnvConfig";
import MainMenuSiteName from "./MainMenuSiteName";
import MainMenuLink from "./MainMenuLink";
import {DashboardTranslKey} from "../dashboard/DashboardTranslKey";
import {FileEarmarkText, HouseDoorFill, PersonBadge} from "../../common/icon/Icon";
import {PersonTranslKey} from "../person/PersonTranslKey";
import MobileMenuListItem from "./MobileMenuListItem";
import MobileMenuList from "./MobileMenuList";
import {MessageTranslKey} from "../message/MessageTranslKey";
import {MessageStateTranslKey} from "../message-state/MessageStateTranslKey";

export default function MobileMainMenu() {
    const [accountDrawerMenuOpen, setAccountDrawerMenuOpen] = useState(false);
    const [appDrawerMenuOpen, setAppDrawerMenuOpen] = useState(false);

    const theme = useTheme();
    const {t} = useTranslation();
    const {authClient} = useAuth();

    const navigate = useNavigate();

    const userRoles = useUserRoles();
    const isAdministrator = UserRoleResolver.isAdministrator(userRoles);
    const isAtLeastOperator = UserRoleResolver.isAtLeastOperator(userRoles);

    const envConfig = useRef(EnvConfiguration.getConfig());

    function getMainTitle(profile: string): string {
        if (profile === PortalProfile.MV) {
            return t(SubmissionTranslKey.SUBMISSION_PORTAL_MV_SHORT);
        } else if (profile === PortalProfile.MO) {
            return t(SubmissionTranslKey.SUBMISSION_PORTAL_MO_SHORT);
        } else {
            return t(SubmissionTranslKey.SUBMISSION_PORTAL_DEFAULT_SHORT);
        }
    }

    function logout() {
        authClient?.logout({redirectUri: envConfig.current.portalUrl});
        setAppDrawerMenuOpen(false);
    }

    function onAppMenuDrawerClicked() {
        setAccountDrawerMenuOpen(false);
        setAppDrawerMenuOpen(!appDrawerMenuOpen);
    }

    function onAppMenuItemClicked(path: string) {
        setAppDrawerMenuOpen(false);
        navigate(path);
    }

    function onAccountMenuDrawerClicked() {
        setAppDrawerMenuOpen(false);
        setAccountDrawerMenuOpen(!accountDrawerMenuOpen);
    }

    function onAccountMenuItemClicked(path: string) {
        setAccountDrawerMenuOpen(false);
        navigate(path);
    }

    const appMenuOptions = (
            <Box pt={7}>
                <MobileMenuList>
                    <MobileMenuListItem title={t(DashboardTranslKey.DASHBOARD)}
                                        icon={HouseDoorFill}
                                        onClick={() => onAppMenuItemClicked(DASHBOARD)} />

                    {isAdministrator &&
                        <MobileMenuListItem title={t(SubmissionTranslKey.RECEIVED_DOCUMENTS)}
                                            icon={FileEarmarkText}
                                            onClick={() => onAppMenuItemClicked(SUBMISSIONS)} />
                    }
                    {isAdministrator &&
                        <MobileMenuListItem title={t(FormSessionTranslKey.FORM_SESSIONS_OVERVIEW)}
                                            icon={PersonBadge}
                                            onClick={() => onAppMenuItemClicked(FORM_SESSIONS)} />
                    }

                    {isAtLeastOperator &&
                        <>
                            <Divider />
                            {isAdministrator && (
                                <>
                                    <MobileMenuListItem title={t(UserTranslKey.USER_OVERVIEW)}
                                                        icon={AccountBox}
                                                        onClick={() => onAppMenuItemClicked(ADMIN_USER_OVERVIEW)} />
                                    <MobileMenuListItem title={t(PersonTranslKey.PERSON_OVERVIEW)}
                                                        icon={Person}
                                                        onClick={() => onAppMenuItemClicked(ADMIN_PERSON_OVERVIEW)} />
                                    <MobileMenuListItem title={t(UserTranslKey.USER_OVERVIEW)}
                                                        icon={AccountBox}
                                                        onClick={() => onAppMenuItemClicked(ADMIN_USER_OVERVIEW)} />
                                    <MobileMenuListItem title={t(MessageTranslKey.ISDS_MESSAGE_OVERVIEW)}
                                                        icon={MailOutline}
                                                        onClick={() => onAppMenuItemClicked(ISDS_MESSAGE_OVERVIEW)} />
                                    <MobileMenuListItem title={t(MessageStateTranslKey.ISDS_STATE_OVERVIEW)}
                                                        icon={AssessmentOutlined}
                                                        onClick={() => onAppMenuItemClicked(ISDS_MESSAGE_STATE_OVERVIEW)} />
                                </>
                            )}
                            <MobileMenuListItem
                                title={t(SubmissionTranslKey.DOCUMENTS_MANAGEMENT)}
                                icon={ArchiveOutlined}
                                onClick={() => onAppMenuItemClicked(ADMIN_SUBMISSIONS)} />

                        </>
                    }
                </MobileMenuList>
            </Box>
    );

    const accountMenuOptions = (
            <Box pt={7}>
                <MobileMenuList>
                    <MobileMenuListItem title={t(UserTranslKey.PROFILE)}
                            icon={AccountCircle}
                            onClick={() => onAccountMenuItemClicked(PROFILE)}/>
                    <MobileMenuListItem title={t(UserTranslKey.LOGOUT)}
                            icon={Logout}
                            onClick={logout}/>
                </MobileMenuList>
            </Box>
    );

    return (
            <>
                <AppBar position={"sticky"} sx={{zIndex: theme.zIndex.drawer + 1}}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs="auto">
                            <AppBarIconButton onClick={onAppMenuDrawerClicked}>
                                <MenuIcon/>
                            </AppBarIconButton>
                        </Grid>

                        <Grid item xs>
                            {/* Empty Grid item to take up the remaining space */}
                        </Grid>

                        <Grid item xs="auto" margin={2}>
                            <MainMenuLink href="/">
                                <MainMenuSiteName>
                                    {getMainTitle(envConfig.current.profile)}
                                </MainMenuSiteName>
                            </MainMenuLink>
                        </Grid>

                        <Grid item xs>
                            {/* Empty Grid item to take up the remaining space */}
                        </Grid>

                        <Grid item xs="auto">
                            <AppBarIconButton onClick={onAccountMenuDrawerClicked}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <AccountCircle/>
                                </Stack>
                            </AppBarIconButton>
                        </Grid>
                    </Grid>
                </AppBar>

                <>
                    <Drawer anchor={"top"} open={appDrawerMenuOpen} onClose={() => setAppDrawerMenuOpen(false)}>
                        {appMenuOptions}
                    </Drawer>

                    <Drawer anchor={"top"} open={accountDrawerMenuOpen} onClose={() => setAccountDrawerMenuOpen(false)}>
                        {accountMenuOptions}
                    </Drawer>
                </>
            </>
    );
}
